import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import get from 'lodash/get';
import cn from 'classnames';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { toast } from 'react-toastify';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
} from 'reactstrap';

import Input from 'components/Form/Input';
import Validator from 'components/Validator/Validator';
import { Button } from 'components/Button';
import { ButtonLink } from 'components/Button/ButtonLink';
import PasswordComplexityChecklist from 'components/PasswordComplexityChecklist/PasswordComplexityChecklist';

import schema from './forgotSchema';
import emailFormSchema from './emailFormSchema';
import style from './style.scss';

const RESET_PASSWORD_SUPPORT_MESSAGE = 'Unable to Reset your password online. Please call us at 888-502-8020 or email us at customerservice@servicingusa.com';

const showResetPasswordError = () => {
  toast.error(
    (
      <div>
        <h5>{RESET_PASSWORD_SUPPORT_MESSAGE}</h5>
      </div>
    ),
    { autoClose: true }
  );
};

const Forgot = (props) => {
  const navigate = useNavigate();
  const { from } = useLocation().state || { from: { pathname: '/' } };
  const { validator: { values, errors } } = props;
  const [redirectToReferrer, setNavigateToReferrer] = useState(false);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const RESEND_DELAY_SECONDS = 60;

  const startResendCountdown = () => setResendCountdown(RESEND_DELAY_SECONDS);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;

    const timerId = setInterval(() => {
      setResendCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timerId);
  }, [resendCountdown]);

  useEffect(() => {
    const resetUser = sessionStorage.getItem('reset-user');
    if (resetUser) {
      sessionStorage.removeItem('reset-user');

      setIsLoading(true);
      resetPassword({ username: resetUser })
        .then(() => {
          setIsLoading(false);
          setStep(2);
          startResendCountdown();
        })
        .catch((err) => {
          setIsLoading(false);
          showResetPasswordError(err);
        });
    }
  }, []);

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value.trim());
  };

  const handleSubmitFrom = (e) => {
    e.preventDefault();
    const { validator: { validate } } = props;

    if (validate(emailFormSchema).isValid) {
      const username = `${values.username}`.toLowerCase();

      setIsLoading(true);
      resetPassword({ username })
        .then(() => {
          setStep(2);
          setIsLoading(false);
          startResendCountdown();
        })
        .catch((err) => {
          setIsLoading(false);
          showResetPasswordError(err);
        });
    } else {
      console.log('api error');
    }
  };

  const handleResetPassword = (data, e) => {
    e.preventDefault();

    const { validator: { validate } } = props;
    if (validate(schema).isValid) {
      setIsLoading(true);
      const username = `${values.username}`.toLowerCase();

      confirmResetPassword({
        username,
        confirmationCode: values.verificationCode,
        newPassword: values.newPassword,
      })
        .then(() => {
          toast.success(
            (
              <div>
                <h5>Great! Your password has been successfully reset. Please use the new password to login</h5>
              </div>
            ),
            { autoClose: true }
          );
          setNavigateToReferrer(true);
        })
        .catch((err) => {
          setIsLoading(false);
          toast.error(
            (
              <div>
                <h5>{get(err, 'message', 'Failed to reset your password.')}</h5>
              </div>
            ),
            { autoClose: true }
          );
        });
    } else {
      console.log('api error');
    }
  };

  const handleResendCode = (e) => {
    e.preventDefault();

    const username = `${values.username}`.toLowerCase();

    setIsLoading(true);
    resetPassword({ username })
      .then(() => {
        setIsLoading(false);
        startResendCountdown();
        toast.success(
          (
            <div>
              <h5>A new verification code has been sent.</h5>
            </div>
          ),
          { autoClose: true }
        );
      })
      .catch((err) => {
        setIsLoading(false);
        showResetPasswordError(err);
      });
  };

  const handleVerificationCodeClick = (e) => {
    e.preventDefault();
    const { validator: { validate } } = props;

    if (validate(emailFormSchema).isValid) {
      setStep(2);
      startResendCountdown();
    } else {
      console.log('api error');
    }
  };

  const handleForgotUsername = (e) => {
    e.preventDefault();
    navigate('/forgot-username');
  };

  if (redirectToReferrer) return <Navigate to={from} />;

  return (
    <form onSubmit={handleSubmitFrom} style={{ minHeight: 650 }} className="d-flex justify-content-center align-items-center">
      <Container fluid>
        <Col sm={{ size: 6, offset: 3 }}>
          <Row>
            <Col>
              <Row>
                <Col>
                  <Card className="animated fadeInUp delay-300ms">
                    <CardHeader>Reset Password</CardHeader>
                    <CardBody>
                      {
                        step === 1
                          ? (
                            <Input
                              name="username"
                              label="Username"
                              onChange={handleInputChange}
                              value={values.username}
                              hasError={!!errors.username}
                              hasValue
                              placeHolder="username"
                              isBadgeVisible={false}
                              isRequired
                              isDisabled={isLoading}
                              errorMessage={errors.username}
                            />
                          )
                          : (
                            <>
                              <Row>
                                <Col sm={12} lg={12}>
                                  <Input
                                    name="verificationCode"
                                    label="Verification Code"
                                    onChange={handleInputChange}
                                    value={values.verificationCode}
                                    hasError={!!errors.verificationCode}
                                    hasValue
                                    placeHolder="Verification Code"
                                    isBadgeVisible={false}
                                    isRequired
                                    errorMessage={errors.verificationCode}
                                  />
                                </Col>
                                <Col sm={12} lg={12}>
                                  <Input
                                    name="newPassword"
                                    type="password"
                                    label="New Password"
                                    onChange={handleInputChange}
                                    value={values.newPassword}
                                    hasError={!!errors.newPassword}
                                    hasValue
                                    placeHolder="New Password"
                                    isBadgeVisible={false}
                                    isRequired
                                    errorMessage={errors.newPassword}
                                  />
                                </Col>
                                <Col sm={12} lg={12}>
                                  <Input
                                    name="confirmPassword"
                                    type="password"
                                    label="Confirm Password"
                                    onChange={handleInputChange}
                                    value={values.confirmPassword}
                                    hasError={!!errors.confirmPassword}
                                    hasValue
                                    placeHolder="Confirm Password"
                                    isBadgeVisible={false}
                                    isRequired
                                    errorMessage={errors.confirmPassword}
                                  />
                                </Col>
                              </Row>
                              <Row>
                                <Col>
                                  <PasswordComplexityChecklist
                                    password={values.newPassword || ''}
                                    confirmPassword={values.confirmPassword || ''}
                                  />
                                </Col>
                              </Row>
                            </>
                          )
                      }
                    </CardBody>
                  </Card>
                  {
                    step === 1
                      ? (
                        <div className="text-center">
                          <Button
                            className={cn('small w-100', style.button)}
                            onClick={handleSubmitFrom}
                            isLoading={isLoading}
                            color="primary"
                          >
                            Send Verification Code
                          </Button>
                          <div className="mt-1">
                            <ButtonLink className="btn-link cursor-pointer" onClick={handleVerificationCodeClick}>Already received a verification code?</ButtonLink>
                          </div>
                          <div className="mt-1">
                            <ButtonLink className="btn-link cursor-pointer" onClick={handleForgotUsername}>Forgot username?</ButtonLink>
                          </div>
                        </div>
                      )
                      : (
                        <div className="text-center">
                          <Button
                            className={cn('small w-100', style.button)}
                            onClick={handleResetPassword.bind(null, values)}
                            isLoading={isLoading}
                            color="primary"
                          >
                            Reset Password
                          </Button>
                          <div className="mt-1">
                            Didn&apos;t receive the code?
                            {' '}
                            {
                              resendCountdown > 0
                                ? (
                                  <span className="text-muted">
                                    Resend in
                                    {' '}
                                    {resendCountdown}
                                    s
                                  </span>
                                )
                                : (
                                  <ButtonLink className="btn-link cursor-pointer" onClick={handleResendCode} isDisabled={isLoading}>Send a new one</ButtonLink>
                                )
                            }
                          </div>
                        </div>
                      )
                  }
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Container>
    </form>
  );
};

Forgot.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(
  Validator(schema),
  connect((state) => ({
    auth: state.auth,
  }))
)(Forgot);
